import * as React from 'react';
import {CSSProperties} from 'react';
import {Table} from 'antd';
import {ErrorAlert} from '../common/ErrorAlert';
import {ColumnProps} from 'antd/lib/table';
import {ErrorResponse, GridRequestFilters, PageResponseEnvelope, RentalItem} from '../api/dtos/dtos';
import furoApi from '../api/FuroApi';
import locales from '../locales';
import * as tableUtils from '../utils/table-utils';
import {GridState, searchableSortableColumn, simpleColumn} from '../utils/table-utils';
import {
  ApplicationContextConsumer,
  bindLoadingHandler,
  LoadingOverlayHandler,
  withLoadingOverlay
} from '../app/ApplicationContext';
import {renderStateElementCondensed} from '../common/state';
import {renderItemName} from "../item/item-name-renderer";
import {renderItemProperties} from "../item/item-property-renderer";

const addressSpanStyle: CSSProperties = {fontSize: 12, marginBottom: 0};
const nameSpanStyle: CSSProperties = {...addressSpanStyle, color: 'var(--color-medium-gray)'};

interface Props {
  excludedFromSearch: RentalItem[];
  clientId: number | null;
  onSelect: (rentalItem: RentalItem) => void,
}

interface State extends GridState<RentalItem> {
  error?: ErrorResponse;
}

class RentalItemSelector extends React.Component<Props, State> {

  public readonly loadingHandler?: LoadingOverlayHandler;

  public readonly state: State = {
    ...tableUtils.initialGridState(10),
    sortColumnKey: 'name',
    sortOrder: 'ascend',
  };

  public componentDidMount() {
    this.reload();
  }

  public render() {
    const columns: Array<ColumnProps<RentalItem>> = [
      {
        ...simpleColumn('', r => renderStateElementCondensed(r.state)),
        key: 'state',
        width: 38,
      },
      searchableSortableColumn(locales.appellation, 'name', this.state, this.renderNameElement),
      searchableSortableColumn(locales.inventoryNumber, 'inventoryNumber', this.state, r => r.item.inventoryNumber),
      searchableSortableColumn(locales.category, 'category', this.state, r => r.item.category),
      searchableSortableColumn(locales.resource, 'resource', this.state, r => r.item.resource),
      searchableSortableColumn(locales.properties, 'details', this.state, r => renderItemProperties(r.item)),
      searchableSortableColumn(locales.comment, 'comment', this.state, r => r.item.comment),
      searchableSortableColumn(locales.client_site, 'site', this.state, this.renderSiteElement),
    ];

    return (
      <>
        <ApplicationContextConsumer>
          {({loadingHandler}) => bindLoadingHandler(loadingHandler, this)}
        </ApplicationContextConsumer>

        <Table
          dataSource={this.state.data}
          columns={columns}
          rowKey={tableUtils.idRowKeyFn}
          bordered={true}
          size="small"
          pagination={tableUtils.defaultPaginationConfig(this.state)}
          onChange={tableUtils.handleOnGridChange(this.state, this.onGridChange)}
          scroll={{x: 1024}}
        />
        <ErrorAlert style={{marginTop: '16px'}} error={this.state.error}/>
      </>
    );
  }

  private renderNameElement = (record: RentalItem) => {
    const onClick = () => this.props.onSelect(record);
    return (<a onClick={onClick}>{renderItemName(record.item)}</a>);
  };

  private renderSiteElement = (record: RentalItem) => {
    const site = record.rentalSite;
    return site && (
      <>
        <p style={addressSpanStyle}>{site.address}</p>
        {site.name && <p style={nameSpanStyle}>{site.name}</p>}
      </>
    );
  };

  private onGridChange = (newState: GridState<RentalItem>) => {
    this.setState({...newState}, () => this.reload());
  };

  private reload = () => {
    const {clientId} = this.props;
    if (!clientId) {
      throw new Error("cliendId is missing!");
    }

    const request = {
      ...tableUtils.toGridRequest(this.state),
      filters: this.addExcludedItemsToFilters(this.state.filters),
    };

    const promise = furoApi.rentalItems.getOngoingForClient(clientId, request)
      .then(this.onReloadSuccess)
      .catch(this.onApiError);

    withLoadingOverlay(promise, this.loadingHandler);
  };

  private addExcludedItemsToFilters = (filters?: GridRequestFilters): GridRequestFilters | undefined => {
    const {excludedFromSearch} = this.props;
    if (!excludedFromSearch) {
      return filters;
    }
    return {
      ...filters,
      excludedIds: excludedFromSearch.map(item => String(item.id)),
    }
  };

  private onReloadSuccess = (response: PageResponseEnvelope<RentalItem>) => {
    this.setState({
      ...tableUtils.toGridState(response),
      error: undefined,
    });
  };

  private onApiError = (error: ErrorResponse) => {
    this.setState({error})
  };

}

export default RentalItemSelector;


// WEBPACK FOOTER //
// ./src/return-details/RentalItemSelector.tsx